import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Tab, Tabs } from 'react-bootstrap';
import CustomTableField from '../../../components/CustomTableField';
import { changesApi } from '../../../services/changesApi';
import type { User, WorkflowCustomField } from '../../../types';
import { ATTACHMENT_ACCEPT, validateAttachmentFile, validateAttachmentFiles } from '../../../utils/uploadValidation';
import { shouldShowConditionalField } from '../../../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../../../utils/currencyInput';
import { createDefaultTableValue } from '../../../utils/tableCustomField';
import {
  INCIDENT_EMPTY_TAB_HINT,
  groupIncidentFields,
  isIncidentFieldRequired,
  findServicosField,
  pickIncidentFieldValues,
  servicoOptionFromForwardQueueName,
  splitComboOptions,
  type IncidentCustomFieldFiles,
  type IncidentCustomFieldValues,
} from '../incidentTicketFields';

const INFO_TAB_KEY = 'informacoes';

interface Props {
  workflowId: number | '';
  incidentsGroupId?: number | null;
  values: IncidentCustomFieldValues;
  files: IncidentCustomFieldFiles;
  onValuesChange: (values: IncidentCustomFieldValues) => void;
  onFilesChange: (files: IncidentCustomFieldFiles) => void;
  onFieldsChange: (fields: WorkflowCustomField[]) => void;
  users?: Array<Pick<User, 'id' | 'name' | 'email'>>;
  ariaPrefix: string;
  infoTab?: React.ReactNode;
  initialValues?: IncidentCustomFieldValues;
  hideFileFields?: boolean;
  /** Nome da fila de encaminhamento (ex.: CINFRA - InfraHosting) para sugerir Serviços. */
  forwardQueueName?: string;
}

const IncidentTicketFieldsForm: React.FC<Props> = ({
  workflowId,
  incidentsGroupId,
  values,
  files,
  onValuesChange,
  onFilesChange,
  onFieldsChange,
  users = [],
  ariaPrefix,
  infoTab,
  initialValues,
  hideFileFields = false,
  forwardQueueName,
}) => {
  const [fields, setFields] = useState<WorkflowCustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(infoTab ? INFO_TAB_KEY : '');
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!workflowId) {
      setFields([]);
      onFieldsChange([]);
      onValuesChange({});
      onFilesChange({});
      setError(null);
      if (infoTab) setActiveTab(INFO_TAB_KEY);
      return;
    }
    if (!incidentsGroupId) {
      setFields([]);
      onFieldsChange([]);
      onValuesChange({});
      onFilesChange({});
      setError(null);
      if (infoTab) setActiveTab(INFO_TAB_KEY);
      return;
    }
    const fetchSeq = ++fetchSeqRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void changesApi
      .listIncidentWorkflowFields(Number(workflowId), incidentsGroupId)
      .then((items) => {
        if (cancelled || fetchSeq !== fetchSeqRef.current) return;
        const nextFields = items || [];
        setFields(nextFields);
        onFieldsChange(nextFields);
        let nextValues = pickIncidentFieldValues(initialValues, nextFields);
        const servicosField = findServicosField(nextFields);
        if (servicosField && !nextValues[servicosField.id]) {
          const suggested = servicoOptionFromForwardQueueName(forwardQueueName, servicosField);
          if (suggested) {
            nextValues = { ...nextValues, [servicosField.id]: suggested };
          }
        }
        onValuesChange(nextValues);
        onFilesChange({});
        const { tabs } = groupIncidentFields(nextFields);
        setActiveTab(infoTab ? INFO_TAB_KEY : tabs[0]?.key || '');
      })
      .catch(() => {
        if (cancelled) return;
        setFields([]);
        onFieldsChange([]);
        setError('Não foi possível carregar as informações da solicitação deste fluxo.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Campos e valores são resetados só quando o fluxo muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, incidentsGroupId, forwardQueueName]);

  const { tabs } = useMemo(() => groupIncidentFields(fields), [fields]);

  const setValue = (fieldId: number, value: unknown) => {
    onValuesChange({ ...values, [fieldId]: value });
  };

  const renderCheckField = (field: WorkflowCustomField, aria: string, description: React.ReactNode) => {
    const value = values[field.id];
    const options = splitComboOptions(field.options);
    const link = field.checkbox_link ? (
      <div className="mb-2">
        <a
          href={field.checkbox_link}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-sm btn-outline-primary"
        >
          {field.checkbox_link_text || 'Ler Diretrizes'}
        </a>
      </div>
    ) : null;

    if (options.length) {
      let selectedValues: string[] = [];
      if (Array.isArray(value)) {
        selectedValues = value.map((item) => String(item));
      } else if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          selectedValues = Array.isArray(parsed) ? parsed.map((item) => String(item)) : [String(parsed)];
        } catch {
          selectedValues = [value];
        }
      }
      const selectedValue = selectedValues[0] || '';
      const shouldShowInfoText = field.checkbox_text_trigger_option
        ? field.allow_multiple
          ? selectedValues.includes(field.checkbox_text_trigger_option)
          : selectedValue === field.checkbox_text_trigger_option
        : field.allow_multiple
          ? selectedValues.length > 0
          : !!selectedValue;
      return (
        <Form.Group key={field.id} className="mb-3">
          {description}
          {link}
          {options.map((option) => (
            <Form.Check
              key={option}
              type={field.allow_multiple ? 'checkbox' : 'radio'}
              name={field.allow_multiple ? undefined : `field-${field.id}`}
              label={option}
              aria-label={`${aria} ${option}`}
              checked={field.allow_multiple ? selectedValues.includes(option) : selectedValue === option}
              onChange={(e) => {
                if (field.allow_multiple) {
                  const next = e.target.checked
                    ? [...selectedValues, option]
                    : selectedValues.filter((item) => item !== option);
                  setValue(field.id, next);
                  return;
                }
                setValue(field.id, e.target.checked ? option : '');
              }}
            />
          ))}
          {field.checkbox_text && shouldShowInfoText ? (
            <div className="alert alert-info mt-2">{field.checkbox_text}</div>
          ) : null}
        </Form.Group>
      );
    }

    const checked = value === true || value === 'true' || value === 1 || value === '1';
    return (
      <Form.Group key={field.id} className="mb-3">
        {description}
        {link}
        <Form.Check
          type="checkbox"
          aria-label={aria}
          label={field.checkbox_label || field.label}
          checked={checked}
          onChange={(e) => setValue(field.id, e.target.checked ? 'true' : 'false')}
        />
        {checked && field.checkbox_text ? (
          <div className="alert alert-info mt-2">{field.checkbox_text}</div>
        ) : null}
      </Form.Group>
    );
  };

  const renderField = (field: WorkflowCustomField) => {
    if (hideFileFields && (field.field_type === 'file' || field.field_type === 'files')) {
      return null;
    }
    const required = isIncidentFieldRequired(field);
    const value = values[field.id];
    const label = (
      <Form.Label>
        {field.label}
        {required ? <span className="text-danger"> *</span> : null}
      </Form.Label>
    );
    const description = field.description ? (
      <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line' }}>
        {field.description}
      </Form.Text>
    ) : null;
    const aria = `${ariaPrefix} ${field.label}`;

    if (field.field_type === 'check') {
      return renderCheckField(field, aria, (
        <>
          {label}
          {description}
        </>
      ));
    }
    if (field.field_type === 'text_long') {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Control
            as="textarea"
            rows={4}
            aria-label={aria}
            value={String(value || '')}
            onChange={(e) => setValue(field.id, e.target.value)}
          />
        </Form.Group>
      );
    }
    if (field.field_type === 'list' || field.field_type === 'combo') {
      const options = splitComboOptions(field.options);
      if (!options.length) {
        return (
          <Form.Group key={field.id} className="mb-3">
            {label}
            {description}
            <Form.Text className="text-muted">Nenhuma opção configurada para este campo.</Form.Text>
          </Form.Group>
        );
      }
      if (field.allow_multiple) {
        const selected = Array.isArray(value) ? (value as string[]) : [];
        return (
          <Form.Group key={field.id} className="mb-3">
            {label}
            {description}
            {options.map((option) => (
              <Form.Check
                key={option}
                type="checkbox"
                label={option}
                aria-label={`${aria} ${option}`}
                checked={selected.includes(option)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, option]
                    : selected.filter((item) => item !== option);
                  setValue(field.id, next);
                }}
              />
            ))}
          </Form.Group>
        );
      }
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Select
            aria-label={aria}
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => setValue(field.id, e.target.value)}
          >
            <option value="">Selecione...</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    }
    if (field.field_type === 'date') {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Control
            type="date"
            aria-label={aria}
            value={String(value || '')}
            onChange={(e) => setValue(field.id, e.target.value)}
          />
        </Form.Group>
      );
    }
    if (field.field_type === 'period') {
      let period = { start: '', end: '' };
      if (value) {
        try {
          period = typeof value === 'string' ? JSON.parse(value) : (value as { start: string; end: string });
        } catch {
          period = { start: '', end: '' };
        }
      }
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <div className="d-flex gap-2">
            <Form.Control
              type="date"
              aria-label={`${aria} início`}
              value={period.start || ''}
              onChange={(e) => setValue(field.id, JSON.stringify({ ...period, start: e.target.value }))}
            />
            <Form.Control
              type="date"
              aria-label={`${aria} fim`}
              value={period.end || ''}
              onChange={(e) => setValue(field.id, JSON.stringify({ ...period, end: e.target.value }))}
            />
          </div>
        </Form.Group>
      );
    }
    if (field.field_type === 'integer' || field.field_type === 'decimal') {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Control
            type="number"
            step={field.field_type === 'integer' ? '1' : '0.01'}
            min="0"
            aria-label={aria}
            value={String(value || '')}
            onChange={(e) => setValue(field.id, e.target.value)}
          />
        </Form.Group>
      );
    }
    if (field.field_type === 'currency') {
      let currencyValue = { currency: 'BRL', value: '' };
      if (value) {
        try {
          currencyValue = typeof value === 'string' ? JSON.parse(value) : (value as typeof currencyValue);
        } catch {
          currencyValue = { currency: 'BRL', value: String(value) };
        }
      }
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <div className="d-flex gap-2">
            <Form.Select
              aria-label={`${aria} moeda`}
              value={currencyValue.currency || 'BRL'}
              onChange={(e) =>
                setValue(field.id, JSON.stringify({ ...currencyValue, currency: e.target.value }))
              }
            >
              <option value="BRL">BRL (R$)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
            </Form.Select>
            <Form.Control
              aria-label={aria}
              value={formatCurrencyInputDisplay(currencyValue.value || '', currencyValue.currency || 'BRL')}
              onChange={(e) => {
                const normalized = normalizeCurrencyInput(e.target.value);
                if (normalized === null) return;
                setValue(field.id, JSON.stringify({ ...currencyValue, value: normalized }));
              }}
            />
          </div>
        </Form.Group>
      );
    }
    if (field.field_type === 'table') {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <CustomTableField
            value={value || createDefaultTableValue()}
            onChange={(next) => setValue(field.id, next)}
          />
        </Form.Group>
      );
    }
    if (field.field_type === 'file' || field.field_type === 'files') {
      const multiple = field.field_type === 'files';
      const current = files[field.id];
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Control
            type="file"
            multiple={multiple}
            accept={ATTACHMENT_ACCEPT}
            aria-label={aria}
            onChange={(e) => {
              const input = e.target as HTMLInputElement;
              const incoming = Array.from(input.files || []);
              const invalid = multiple
                ? validateAttachmentFiles(incoming)
                : incoming[0]
                  ? validateAttachmentFile(incoming[0])
                  : null;
              if (invalid) {
                input.value = '';
                return;
              }
              onFilesChange({
                ...files,
                [field.id]: multiple ? incoming : incoming[0] || null,
              });
            }}
          />
          {current ? (
            <small className="text-muted d-block mt-1">
              {Array.isArray(current) ? `${current.length} arquivo(s) selecionado(s)` : current.name}
            </small>
          ) : null}
        </Form.Group>
      );
    }
    if (
      field.field_type === 'user' ||
      field.field_type === 'analyst' ||
      field.field_type === 'approver' ||
      field.field_type === 'contract_manager_approver' ||
      field.field_type === 'group'
    ) {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          {description}
          <Form.Select
            aria-label={aria}
            value={String(value || '')}
            onChange={(e) => setValue(field.id, e.target.value)}
          >
            <option value="">Selecione...</option>
            {users.map((user) => (
              <option key={user.id} value={String(user.id)}>
                {user.name} {user.email ? `(${user.email})` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    }
    if (
      field.field_type === 'calculation' ||
      field.field_type === 'platform_document' ||
      field.field_type === 'technical_proposal_review'
    ) {
      return (
        <Form.Group key={field.id} className="mb-3">
          {label}
          <Form.Text className="text-muted d-block">
            Este campo é preenchido no módulo de Incidentes depois que o chamado existir.
          </Form.Text>
        </Form.Group>
      );
    }
    return (
      <Form.Group key={field.id} className="mb-3">
        {label}
        {description}
        <Form.Control
          type={field.field_type === 'link' ? 'url' : 'text'}
          aria-label={aria}
          value={String(value || '')}
          onChange={(e) => setValue(field.id, e.target.value)}
        />
      </Form.Group>
    );
  };

  const renderCustomTab = (tab: { key: string; title: string; fields: WorkflowCustomField[] }) => {
    const visible = tab.fields.filter(
      (field) =>
        shouldShowConditionalField(field, fields, values) &&
        !(hideFileFields && (field.field_type === 'file' || field.field_type === 'files')),
    );
    return (
      <Tab key={tab.key} eventKey={tab.key} title={tab.title}>
        <div className="pt-3">
          {visible.length ? (
            visible.map(renderField)
          ) : (
            <div className="text-muted mb-3">
              <small>{INCIDENT_EMPTY_TAB_HINT}</small>
            </div>
          )}
        </div>
      </Tab>
    );
  };

  if (!workflowId && !infoTab) return null;

  const customTabsReady = Boolean(workflowId) && !loading && !error && fields.length > 0;
  const tabKey = activeTab || (infoTab ? INFO_TAB_KEY : tabs[0]?.key || INFO_TAB_KEY);

  if (!infoTab && !workflowId) return null;
  if (!infoTab && loading) {
    return <p className="small text-muted mt-2">Carregando informações da solicitação...</p>;
  }
  if (!infoTab && error) {
    return (
      <Alert variant="warning" className="mt-2 mb-0">
        {error}
      </Alert>
    );
  }
  if (!infoTab && !fields.length) return null;

  return (
    <Tabs
      activeKey={tabKey}
      onSelect={(key) => setActiveTab(key || (infoTab ? INFO_TAB_KEY : ''))}
      className="mt-3"
    >
      {infoTab ? (
        <Tab eventKey={INFO_TAB_KEY} title="Informações">
          <div className="pt-3">
            {infoTab}
            {loading ? (
              <p className="small text-muted mt-2">Carregando informações da solicitação...</p>
            ) : null}
            {error ? (
              <Alert variant="warning" className="mt-2 mb-0">
                {error}
              </Alert>
            ) : null}
          </div>
        </Tab>
      ) : null}
      {customTabsReady ? tabs.map(renderCustomTab) : null}
    </Tabs>
  );
};

export default IncidentTicketFieldsForm;
